import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useFragment, useMutation } from "react-relay";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { AnswerOptionV2Input } from "@relay/CreateMultipleChoiceElementButton_CreateMultipleChoiceElementV2Mutation.graphql";
import { multipleChoiceElementForm_EditMultipleChoiceElementMutation } from "@relay/multipleChoiceElementForm_EditMultipleChoiceElementMutation.graphql";
import {
	AnsweringTypeV2,
	multipleChoiceElementForm_MultipleChoiceElementV2Fragment$key,
} from "@relay/multipleChoiceElementForm_MultipleChoiceElementV2Fragment.graphql";
import {
	EDIT_MULTIPLE_CHOICE_ELEMENT_MUTATION,
	MULTIPLE_CHOICE_ELEMENTV2_FRAGMENT,
} from "./multiple-choice-element-form.graphql";
import { FormState, OwnProps } from "./multiple-choice-element-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import {
	resetArrayOfEditedForms,
	addEditedFormToEditedFormsArray,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { stripHtml } from "../../../../util/html.utils";
import { AnswerOptionsField } from "../AnswerOptionsField";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const MultipleChoiceElementForm = ({
	multipleChoiceElementFragmentRef,
	onBack,
}: OwnProps) => {
	const multipleChoiceElement =
		useFragment<multipleChoiceElementForm_MultipleChoiceElementV2Fragment$key>(
			MULTIPLE_CHOICE_ELEMENTV2_FRAGMENT,
			multipleChoiceElementFragmentRef,
		);
	const [editMultipleChoiceElement, isEditingMultipleChoiceElement] =
		useMutation<multipleChoiceElementForm_EditMultipleChoiceElementMutation>(
			EDIT_MULTIPLE_CHOICE_ELEMENT_MUTATION,
		);
	const { t } = useTranslation(["common", "validation"]);
	const setDraft = useDraftStatusUpdater();
	const dispatch = useTypedDispatch();
	const formId = "MultipleChoiceElementForm";
	const formik = useFormik<FormState>({
		initialValues: {
			question: stripHtml(multipleChoiceElement.question),
			imageOpt: (multipleChoiceElement.image as FileV2) || undefined,
			answeringType: multipleChoiceElement.answeringType,
			answeringTypeText: multipleChoiceElement.answeringTypeText || undefined,
			answerOptions: [
				...multipleChoiceElement.answerOptions.map((ao) => ({
					answer: stripHtml(ao.answer),
					isCorrect: ao.isCorrect,
					imageRef: ao.image?.id,
					image: ao.image as FileV2,
				})),
			],
		},
		validationSchema: Yup.object().shape({
			question: Yup.string().required(t("validation:questionFieldRequired")),
			answeringType: Yup.string().required(t("validation:answeringTypeRequired")),
			answerOptions: Yup.array()
				.required(t("validation:answerOptionsRequired"))
				.min(1, t("validation:atLeastOneAnswer"))
				.test("test2", "test2", function (value) {
					const options = value as AnswerOptionV2Input[];
					if (!options.find((ao) => ao.isCorrect)) {
						return this.createError({
							path: "answerOptions",
							message: t("validation:atLeastOneCorrectAnswer"),
						});
					}
					if (options.find((o) => !o.answer)) {
						return this.createError({
							path: "answerOptions",
							message: t("validation:everyAnswerNeedsText"),
						});
					}
					if (new Set(options.map((o) => o.answer)).size < options.length) {
						return this.createError({
							path: "answerOptions",
							message: t("validation:everyAnswerMustBeUnique"),
						});
					}
					return true;
				})
				.typeError(t("validation:validationError")),
		}),
		onSubmit: (values) => {
			editMultipleChoiceElement({
				variables: {
					input: {
						multipleChoiceElementId: multipleChoiceElement.id,
						title: values.question,
						question: values.question,
						imageIdOpt: values.imageOpt?.id || undefined,
						answeringType: values.answeringType,
						answeringTypeText: values.answeringTypeText,
						answerOptions: values.answerOptions.map((o) => ({
							answer: o.answer,
							isCorrect: o.isCorrect,
							imageRef: o.imageRef,
						})),
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
				},
				updater: setDraft,
			});
		},
	});

	const canUpload = useHasPermissions(["UserInAccountPermission_Nodes_UpdateNodes"]);
	const canDelete = useHasPermissions("onlyOwnerOfRoot");

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<ValidatedField<FormState, string>
				name={"question"}
				label={t("general.title")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={DefaultTextFieldComponent}
				formikConfig={formik}
			/>

			<ValidatedField<FormState, FileV2>
				name={"imageOpt"}
				label={t("forms.questionImage")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({ fieldName, fieldValue, updateField, onChange }) => (
					<FileSelectionField
						name={fieldName}
						selectedFile={fieldValue}
						setSelectedFile={updateField}
						filterByFileTypes={["image/png", "image/jpg", "image/jpeg"]}
						onChange={onChange}
						canUploadFiles={canUpload}
						canDeleteFiles={canDelete}
					/>
				)}
				formikConfig={formik}
			/>

			<ValidatedField<FormState, AnsweringTypeV2>
				name={"answeringType"}
				label={t("forms.allowedAnswers")}
				component={({ fieldName, fieldValue, updateField }) => {
					return (
						<div>
							<Dropdown
								name={fieldName}
								value={fieldValue}
								options={[
									{
										label: t("educationalOffer.allowOnlyOneAnswer"),
										value: "allowOnlyOneAnswer",
									},
									{
										label: t("educationalOffer.allowMultipleAnswers"),
										value: "allowMultipleAnswersAllCorrectMustBeGiven",
									},
								]}
								onChange={(e) => {
									updateField(e.value);
									dispatch(addEditedFormToEditedFormsArray({ form: formId }));
								}}
							/>
						</div>
					);
				}}
				formikConfig={formik}
			/>

			<ValidatedField<FormState, string>
				name={"answeringTypeText"}
				label={t("forms.task")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={DefaultTextFieldComponent}
				formikConfig={formik}
			/>

			<ValidatedField<FormState, AnswerOptionV2Input[]>
				name={"answerOptions"}
				label={t("forms.answerOptions")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({ fieldValue, updateField, onChange }) => (
					<AnswerOptionsField
						answerOptions={fieldValue || []}
						onUpdate={updateField}
						onChange={onChange}
						canUploadFiles={canUpload}
						canDeleteFiles={canDelete}
					/>
				)}
				formikConfig={formik}
			/>

			<Button
				disabled={
					Object.entries(formik.touched).length === 0 || isEditingMultipleChoiceElement
				}
				type="submit"
				label={t("actions.save")}
				className="mt-2"
			/>

			<Button
				type="button"
				disabled={!formik.isValid}
				onClick={() => {
					onBack();
				}}
				label={t("actions.back")}
				className="p-button-secondary mt-2"
			/>
		</form>
	);
};
